import { Directive, ElementRef, HostListener, Input } from '@angular/core'
import { NgControl } from '@angular/forms'

@Directive({
  selector: '[vdrNumberFormat]'
})
export class NumberFormatDirective {
  @Input('vdrNumberFormat') config: {
    thousandSeparator: string
    decimalSeparator: string
    max?: number
    min?: number
  } = {
    thousandSeparator: ' ',
    decimalSeparator: '.',
    max: undefined,
    min: undefined
  }

  constructor(
    private el: ElementRef,
    private control: NgControl
  ) {}

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    // Allow: backspace, delete, tab, escape, enter, decimal point
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Enter',
      'Escape',
      'ArrowLeft',
      'ArrowRight'
    ]
    if (
      allowedKeys.includes(event.key) ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (event.ctrlKey === true &&
        ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase()))
    ) {
      return
    }

    // Block non-numeric input
    if (!/^\d$/.test(event.key)) {
      event.preventDefault()
    }
  }

  @HostListener('input', ['$event'])
  onInput() {
    this.formatValue()
  }

  private formatValue() {
    const input = this.el.nativeElement as HTMLInputElement
    const rawValue = input.value
    const selStart = input.selectionStart ?? rawValue.length
    const selEnd = input.selectionEnd ?? rawValue.length

    // Count how many separator chars exist BEFORE the cursor in the old value
    const beforeCursor = rawValue.slice(0, selStart)
    const separatorsBeforeCursor = (beforeCursor.match(/\D/g) || []).length

    let value = rawValue.replace(/\D/g, '')

    if (value) {
      const numValue = Number(value)
      if (this.config.max !== undefined && numValue > this.config.max) {
        value = this.config.max.toString()
      }
      if (this.config.min !== undefined && numValue < this.config.min) {
        value = this.config.min.toString()
      }

      value = value.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        this.config.thousandSeparator
      )
      input.value = value

      if (this.control.control) {
        this.control.control.setValue(value, { emitEvent: false })
      }

      // Restore cursor: count digits before old cursor, find same digit position in new value
      const digitsBeforeCursor = selStart - separatorsBeforeCursor
      let newCursorPos = 0
      if (digitsBeforeCursor > 0) {
        let digitCount = 0
        newCursorPos = value.length
        for (let i = 0; i < value.length; i++) {
          if (/\d/.test(value[i])) {
            digitCount++
          }
          if (digitCount === digitsBeforeCursor) {
            newCursorPos = i + 1
            break
          }
        }
      }
      input.setSelectionRange(newCursorPos, newCursorPos)
    } else {
      input.value = ''
      if (this.control.control) {
        this.control.control.setValue('', { emitEvent: false })
      }
    }
  }

  @HostListener('blur')
  onBlur() {
    const input = this.el.nativeElement
    const value = input.value.replace(/[^\d]/g, '')

    if (value.length > 0) {
      input.value = value.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        this.config.thousandSeparator
      )
    }
  }
}
